import {
  Box,
  Container,
  Stack,
  SimpleGrid,
  Text,
  useColorModeValue,
  Heading,
  Image,
  Button,
} from "@chakra-ui/react";
import {
  FaTwitter,
  FaYoutube,
  FaInstagram,
  FaLinkedin,
  FaGithub,
  FaFacebook,
  FaTiktok,
  FaHome,
  FaBuilding,
  FaLinkedinIn,
} from "react-icons/fa";
import MainLogo from "../images/logo.png";
import { Link } from "react-router-dom";

export default function Footer() {
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverColor = useColorModeValue("green.500", "green.300");
  const cardBg = useColorModeValue("white", "gray.800");

  return (
    <Box as="footer" mt="170px" position="relative">
      {/* Decorative elements for 3D effect */}
      <Box
        position="absolute"
        top="-50px"
        left="0"
        right="0"
        height="50px"
        bgGradient={`linear(to-b, transparent, ${bgColor})`}
        zIndex="1"
      />
      
      <Box
        bg={bgColor}
        color={textColor}
        position="relative"
        pt="60px"
        pb="0"
        boxShadow="0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)"
      >
        <Container maxW="7xl" px={{ base: 5, md: 10 }}>
          <SimpleGrid
            columns={{ base: 1, sm: 2, md: 4 }}
            spacing={10}
            py={10}
          >
            {/* Logo and Social */}
            <Stack spacing={6}>
              <Box
                p={4}
                bg={cardBg}
                borderRadius="lg"
                boxShadow="lg"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl",
                }}
              >
                <Image
                  width="170px"
                  height="50px"
                  src={MainLogo}
                  alt="EstateGhar"
                  mx="auto"
                  filter="drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))"
                />
                <Text fontSize="sm" mt={4} textAlign="center">
                  © 2025 EstateGhar, All rights reserved
                </Text>
                <Stack direction="row" spacing={4} justify="center" mt={4}>
                  {[
                    { icon: <FaYoutube size={20} />, href: "https://www.youtube.com/channel/UCnNbBmTVDdzAKzMGkjAAX4A", label: "Properties" },
                    { icon: <FaLinkedinIn size={20} />, href: "https://www.linkedin.com/company/estateghar-buy-sell-rent-properties/?viewAsMember=true", label: "Agencies" },
                    { icon: <FaFacebook size={20} />, href: "https://www.facebook.com/estateghar2026", label: "Facebook" },
                    { icon: <FaInstagram size={20} />, href: "https://www.instagram.com/estateghar2026/", label: "Instagram" },
                  ].map((social, index) => (
                    <Box
                      key={index}
                      as="a"
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      p={2}
                      borderRadius="full"
                      bg="white"
                      color="gray.700"
                      boxShadow="md"
                      transition="all 0.2s ease"
                      _hover={{
                        transform: "scale(1.1)",
                        color: hoverColor,
                        boxShadow: "0 0 15px rgba(72, 187, 120, 0.5)",
                      }}
                      title={social.label}
                    >
                      {social.icon}
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Stack>

            {/* Support Links */}
            <Box>
              <Box
                p={5}
                bg={cardBg}
                borderRadius="lg"
                boxShadow="lg"
                h="100%"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl",
                }}
              >
                <Heading as="h5" size="sm" mb={6} color={hoverColor}>
                  Support
                </Heading>
                <Stack spacing={3}>
                  {[
                    { label: "Privacy Policy", to: "/privacy-policy" },
                    { label: "Terms of Service", to: "/terms" },
                    { label: "Report Fake Listing", to: "#" },
                    { label: "Contact Support", to: "#" },
                  ].map((link, index) => (
                    <Link
                      key={index}
                      to={link.to}
                      style={{ textDecoration: "none" }}
                    >
                      <Text
                        fontSize="sm"
                        transition="all 0.2s ease"
                        _hover={{
                          color: hoverColor,
                          transform: "translateX(5px)",
                        }}
                      >
                        {link.label}
                      </Text>
                    </Link>
                  ))}
                </Stack>
              </Box>
            </Box>

            {/* EstateGhar Links */}
            <Box>
              <Box
                p={5}
                bg={cardBg}
                borderRadius="lg"
                boxShadow="lg"
                h="100%"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl",
                }}
              >
                <Heading as="h5" size="sm" mb={6} color={hoverColor}>
                  Quick Links
                </Heading>
                <Stack spacing={3}>
                  {[
                    { label: "List Property", to: "/create-listing" },
                    { label: "Browse Properties", to: "/category/sale" },
                    { label: "Find Rentals", to: "/category/rent" },
                    { label: "Real Estate Agents", to: "/featured-agents" },
                  ].map((link, index) => (
                    <Link
                      key={index}
                      to={link.to}
                      style={{ textDecoration: "none" }}
                    >
                      <Text
                        fontSize="sm"
                        transition="all 0.2s ease"
                        _hover={{
                          color: hoverColor,
                          transform: "translateX(5px)",
                        }}
                      >
                        {link.label}
                      </Text>
                    </Link>
                  ))}
                </Stack>
              </Box>
            </Box>

            {/* About Us */}
            <Box>
              <Box
                p={5}
                bg={cardBg}
                borderRadius="lg"
                boxShadow="lg"
                h="100%"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl",
                }}
              >
                <Heading as="h5" size="sm" mb={6} color={hoverColor}>
                  About Us
                </Heading>
                <Text fontSize="sm" mb={3}>
                  EstateGhar by SafoLifeTech
                </Text>
                <Text fontSize="xs" color="gray.600" mb={4}>
                  Your trusted partner for finding the perfect property. We connect buyers, sellers, and renters across Pakistan.
                </Text>
                <Box
                  mt={6}
                  p={3}
                  bgGradient="linear(to-r, green.400, green.600)"
                  borderRadius="md"
                  color="white"
                  textAlign="center"
                  boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
                >
                  <Text fontSize="xs" fontWeight="bold">
                    FIND YOUR DREAM PROPERTY
                  </Text>
                </Box>
              </Box>
            </Box>
          </SimpleGrid>
        </Container>

        {/* Bottom Bar */}
        <Box
          borderTopWidth={1}
          borderStyle="solid"
          borderColor={borderColor}
          position="relative"
          _before={{
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "2px",
            bgGradient: "linear(to-r, transparent, green.400, transparent)",
          }}
        >
          <Container maxW="7xl" py={4}>
            <Text textAlign="center" fontSize="sm">
              © {new Date().getFullYear()} EstateGhar. All rights reserved. By{" "}
              <Box
                as="span"
                color="green.500"
                fontWeight="bold"
                display="inline-block"
                position="relative"
                _after={{
                  content: '""',
                  position: "absolute",
                  bottom: "-2px",
                  left: 0,
                  right: 0,
                  height: "2px",
                  bgGradient: "linear(to-r, green.500, transparent)",
                }}
              >
                <a href="#" style={{ textDecoration: "none" }}>
                  SafoLifeTech Limited
                </a>
              </Box>
            </Text>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}